/* eslint-disable max-lines */
import { AuxGridColumnType } from "@blk/aladdin-react-components-es";
import { getVariationClassName } from "../table/cell/helpers/variation-helper";
import { buildMultiValuePopupData } from "./multi-value-popup-helpers";
import { moneyComparator, getDateComparator, getDateTimeComparator, performancePeriodComparator, performancePeriodDefaultComparator, areaComparator, unitComparator, idToNameComparator } from "./comparator-helpers";
import { getCommonQuickFilterText } from "./filter-helpers";
import { customAggFuncs } from "./grid-agg-funcs";
import { benchmarkQuartileRankingValueFormatter, getBenchmarkQuartileCellClass } from "./renderers/benchmarkQuartileRenderer";
import { sparklineTooltipRenderer } from "./renderers/sparkline-tooltip-render";
import { dateFormatter, percentageFormatter, multiplyFormatter, moneyFormatter, areaFormatter, dateBasedYearFormatter, idToNameFormatter, yearsFormatter, unitFormatter, dateTimeFormatter, userSettingAlignedDateFormatter, textFormatter } from "./value-formatters";
import { dateBasedYearGetter, moneyGetter, areaGetter, multiplyGetter, percentageGetter, linkLabelValueGetter, unitGetter, booleanGetter } from "./value-getters";
import { getEsgComparator } from "common/esg-helpers";
import { convertToValidCssClass } from "common/string-helper";
import { DateFilterOptions, NumberFilterOptions } from "components/config/grid-templates/predefined-filters";
import { language } from "language/default";
import { ChartSelectValueFormatKind } from "store/current-dashboard/types";
const rightAlignCellClassName = "right-aligned-cell";
const rightAlignHeaderClassName = "right-aligned-header";
const ratingCellClass = (params) => `rating-cell ${convertToValidCssClass(`rating-cell-${params.value}`)}`;
const variationCellClass = (params) => getVariationClassName(parseFloat(params.value));
const benchmarkQuartileCellClass = (params) => getBenchmarkQuartileCellClass(parseFloat(params.value));
const rightAlignedVariationCellClass = (params) => `${rightAlignCellClassName} ${variationCellClass(params)}`;
export const CustomColumnType = {
    ...AuxGridColumnType,
    InsAreaColumn: "insAreaColumn",
    InsBasicPerformancePeriodColumn: "insBasicPerformancePeriodColumn",
    InsBooleanToTextColumn: "insBooleanToTextColumn",
    InsCenterAllignedColumn: "insCenterAllignedColumn",
    InsChangeBenchmarkButtonColumn: "insChangeBenchmarkButtonColumn",
    InsCheckBoxColumn: "insCheckBoxColumn",
    InsDatapointLinkColumn: "insDatapointLinkColumn",
    InsDateBasedYearColumn: "insDateBasedYearColumn",
    InsDateColumn: "insDateColumn",
    InsDateGroupColumn: "insDateGroupColumn",
    InsDateTimeColumn: "insDateTimeColumn",
    InsEditableColumn: "insEditableColumn",
    InsExternalLinkIconColumn: "insExternalLinkIconColumn",
    InsGaugeColumn: "insGaugeColumn",
    InsIdToNameColumn: "insIdToNameColumn",
    InsInspectDataValueColumn: "insInspectDataValueColumn",
    InsLinkColumn: "insLinkColumn",
    InsMoneyColumn: "insMoneyColumn",
    InsMultiValuePopupColumn: "insMultiValuePopupColumn",
    InsMultiplyColumn: "insMultiplyColumn",
    InsNumberColumn: "insNumberColumn",
    InsNumberSetColumn: "insNumberSetColumn",
    InsPercentageColumn: "insPercentageColumn",
    InsPercentageVariationColumn: "insPercentageVariationColumn",
    InsPerformancePeriodColumn: "insPerformancePeriodColumn",
    InsPortfolioKindColumn: "insPortfolioKindColumn",
    InsPreqinProColumn: "insPreqinProColumn",
    InsQuartileRanking: "insQuartileRanking",
    InsRepRiskColumn: "insRepRiskColumn",
    InsRightAlignedVariationColumn: "insRightAlignedVariationColumn",
    InsRightBorderedColumn: "insRightBorderedColumn",
    InsSparklineColumn: "insSparklineColumn",
    InsTagsColumn: "InsTagsColumn",
    InsTextSetColumn: "insTextSetColumn",
    InsTransposedColumn: "InsTransposedColumn",
    InsUnitColumn: "insUnitColumn",
    InsUserDateColumn: "insUserDateColumn",
    InsVariationColumn: "insVariationColumn",
    InsYearsColumn: "insYearsColumn"
};
export const formatToColumnTypes = {
    amount: CustomColumnType.InsMoneyColumn,
    area: CustomColumnType.InsAreaColumn,
    boolean: CustomColumnType.InsBooleanToTextColumn,
    date: CustomColumnType.InsDateColumn,
    dateTime: CustomColumnType.InsDateTimeColumn,
    double: CustomColumnType.InsNumberColumn,
    entity: CustomColumnType.InsDatapointLinkColumn,
    esg: CustomColumnType.InsRepRiskColumn,
    multiple: CustomColumnType.InsMultiplyColumn,
    percent: CustomColumnType.InsPercentageColumn,
    performancePeriod: CustomColumnType.InsBasicPerformancePeriodColumn,
    quartile: CustomColumnType.InsQuartileRanking,
    string: CustomColumnType.AUX_TEXT_COLUMN,
    unit: CustomColumnType.InsUnitColumn,
    value: CustomColumnType.InsNumberColumn,
    year: CustomColumnType.InsYearsColumn
};
export const ColumnTypesToChartSelectValueFormatKind = {
    [CustomColumnType.InsMoneyColumn]: ChartSelectValueFormatKind.compactAmount,
    [CustomColumnType.InsAreaColumn]: ChartSelectValueFormatKind.area,
    [CustomColumnType.InsUnitColumn]: ChartSelectValueFormatKind.unit,
    [CustomColumnType.InsBooleanToTextColumn]: ChartSelectValueFormatKind.boolean,
    [CustomColumnType.InsDateColumn]: ChartSelectValueFormatKind.date,
    [CustomColumnType.InsDateTimeColumn]: ChartSelectValueFormatKind.dateTime,
    [CustomColumnType.InsRepRiskColumn]: ChartSelectValueFormatKind.esg,
    [CustomColumnType.InsMultiplyColumn]: ChartSelectValueFormatKind.multiple,
    [CustomColumnType.InsPercentageColumn]: ChartSelectValueFormatKind.percent,
    [CustomColumnType.InsBasicPerformancePeriodColumn]: ChartSelectValueFormatKind.value,
    [CustomColumnType.InsQuartileRanking]: ChartSelectValueFormatKind.esg,
    [CustomColumnType.AUX_TEXT_COLUMN]: ChartSelectValueFormatKind.string,
    [CustomColumnType.InsNumberColumn]: ChartSelectValueFormatKind.compact,
    [CustomColumnType.InsYearsColumn]: ChartSelectValueFormatKind.string,
    [CustomColumnType.InsDatapointLinkColumn]: ChartSelectValueFormatKind.entity
};
export const getSparklineColor = (theme) => {
    if (theme === "light") {
        return "#1B1D20";
    }
    return "#FFFFFF";
};
export const filterParamsTopBottom = {
    buttons: ["reset"],
    filterOptions: [
        NumberFilterOptions.equals,
        NumberFilterOptions.notEqual,
        NumberFilterOptions.greaterThan,
        NumberFilterOptions.greaterThanOrEqual,
        NumberFilterOptions.lessThan,
        NumberFilterOptions.lessThanOrEqual,
        NumberFilterOptions.inRange,
        NumberFilterOptions.blank,
        NumberFilterOptions.notBlank,
        {
            displayKey: NumberFilterOptions.top,
            displayName: "Top",
            predicate: () => true
        },
        {
            displayKey: NumberFilterOptions.bottom,
            displayName: "Bottom",
            predicate: () => true
        }
    ]
};
export const customComparator = (a, b) => {
    const aStr = String(a ?? "").toLowerCase();
    const bStr = String(b ?? "").toLowerCase();
    return aStr.localeCompare(bStr);
};
const sharedDatapointLinkProperties = {
    cellRenderer: "DatapointLinkCellRenderer",
    comparator: customComparator,
    filter: "agTextColumnFilter",
    filterParams: {
        buttons: ["reset"]
    },
    getQuickFilterText: getCommonQuickFilterText
};
const portfolioKindMap = {
    Analytical: language.en.modal.portfolioCreation.analyticalPortfolio,
    Private: language.en.modal.portfolioCreation.privatePortfolio
};
// eslint-disable-next-line max-lines-per-function
export const getCustomColumnTypes = (theme, noTopAndBottomFilter) => {
    const sparklineColor = getSparklineColor(theme);
    const customColumnTypes = {
        [CustomColumnType.InsBasicPerformancePeriodColumn]: {
            comparator: performancePeriodDefaultComparator
        },
        [CustomColumnType.InsTransposedColumn]: {
            cellClass: rightAlignCellClassName,
            headerClass: rightAlignHeaderClassName
        },
        [CustomColumnType.InsDateColumn]: {
            cellClass: rightAlignCellClassName,
            comparator: getDateComparator(),
            filter: "agDateColumnFilter",
            filterParams: {
                buttons: ["reset"],
                comparator: getDateComparator(true),
                filterOptions: [
                    DateFilterOptions.equals,
                    {
                        displayKey: DateFilterOptions.notEqual,
                        displayName: language.en.filter.notEqual,
                        test: getDateComparator(true)
                    },
                    DateFilterOptions.greaterThan,
                    DateFilterOptions.lessThan,
                    DateFilterOptions.inRange
                ],
                includeBlanksInLessThan: true,
                valueFormatter: dateFormatter
            },
            headerClass: rightAlignHeaderClassName,
            type: CustomColumnType.AUX_DATE_COLUMN,
            valueFormatter: dateFormatter
        },
        [CustomColumnType.InsDateTimeColumn]: {
            cellClass: rightAlignCellClassName,
            comparator: getDateTimeComparator(),
            filter: "agDateColumnFilter",
            filterParams: {
                buttons: ["reset"],
                comparator: getDateTimeComparator(true),
                filterOptions: [
                    DateFilterOptions.equals,
                    {
                        displayKey: DateFilterOptions.notEqual,
                        displayName: language.en.filter.notEqual,
                        test: getDateTimeComparator(true)
                    },
                    DateFilterOptions.greaterThan,
                    DateFilterOptions.lessThan,
                    DateFilterOptions.inRange
                ],
                includeBlanksInLessThan: true,
                valueFormatter: dateTimeFormatter
            },
            headerClass: rightAlignHeaderClassName,
            type: CustomColumnType.AUX_DATE_TIME_COLUMN,
            valueFormatter: dateTimeFormatter
        },
        [CustomColumnType.InsDateGroupColumn]: {
            cellClass: rightAlignCellClassName,
            comparator: getDateComparator(),
            headerClass: rightAlignHeaderClassName,
            type: CustomColumnType.AUX_DATE_COLUMN,
            valueFormatter: dateFormatter
        },
        [CustomColumnType.InsGaugeColumn]: {
            cellRenderer: "GaugeCellRenderer",
            comparator: customComparator,
            filter: "agTextColumnFilter"
        },
        [CustomColumnType.InsLinkColumn]: {
            cellRenderer: "LinkOrLabelCellRenderer",
            comparator: customComparator,
            filter: "agTextColumnFilter",
            filterParams: {
                buttons: ["reset"]
            },
            filterValueGetter: linkLabelValueGetter,
            getQuickFilterText: getCommonQuickFilterText,
            valueGetter: linkLabelValueGetter
        },
        [CustomColumnType.InsDatapointLinkColumn]: {
            ...sharedDatapointLinkProperties
        },
        [CustomColumnType.InsMoneyColumn]: {
            cellClass: rightAlignCellClassName,
            cellRenderer: "MoneyCellRenderer",
            comparator: moneyComparator,
            filter: "agNumberColumnFilter",
            filterParams: noTopAndBottomFilter ? undefined : filterParamsTopBottom,
            filterValueGetter: moneyGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: moneyFormatter
        },
        [CustomColumnType.InsMultiplyColumn]: {
            cellClass: rightAlignCellClassName,
            filter: "agNumberColumnFilter",
            filterParams: noTopAndBottomFilter ? undefined : filterParamsTopBottom,
            filterValueGetter: multiplyGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: multiplyFormatter
        },
        [CustomColumnType.InsNumberColumn]: {
            cellClass: rightAlignCellClassName,
            cellRenderer: "NumberCellRenderer",
            filter: "agNumberColumnFilter",
            filterParams: {
                buttons: ["reset"]
            },
            headerClass: rightAlignHeaderClassName
        },
        [CustomColumnType.InsPercentageColumn]: {
            cellClass: rightAlignCellClassName,
            cellRenderer: "PercentageCellRenderer",
            filter: "agNumberColumnFilter",
            filterParams: noTopAndBottomFilter ? undefined : filterParamsTopBottom,
            filterValueGetter: percentageGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: percentageFormatter
        },
        [CustomColumnType.InsPercentageVariationColumn]: {
            cellClass: rightAlignedVariationCellClass,
            filter: "agNumberColumnFilter",
            filterParams: noTopAndBottomFilter ? undefined : filterParamsTopBottom,
            filterValueGetter: percentageGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: percentageFormatter
        },
        [CustomColumnType.InsRepRiskColumn]: {
            cellClass: ratingCellClass,
            comparator: getEsgComparator((value) => value),
            filter: "agTextColumnFilter"
        },
        [CustomColumnType.InsRightAlignedVariationColumn]: {
            cellClass: rightAlignedVariationCellClass
        },
        [CustomColumnType.InsSparklineColumn]: {
            cellClass: "sparkline-in-cell",
            cellRenderer: "agSparklineCellRenderer",
            cellRendererParams: {
                sparklineOptions: {
                    axis: {
                        type: "date"
                    },
                    crosshairs: {
                        xLine: {
                            enabled: false
                        }
                    },
                    highlightStyle: {
                        fill: sparklineColor,
                        size: 5,
                        stroke: sparklineColor
                    },
                    line: {
                        stroke: sparklineColor
                    },
                    tooltip: {
                        renderer: sparklineTooltipRenderer
                    },
                    type: "line",
                    xKey: "x",
                    yKey: "y"
                }
            },
            filter: false
        },
        [CustomColumnType.InsIdToNameColumn]: {
            aggFunc: customAggFuncs.idToNameAggFunc,
            comparator: idToNameComparator,
            valueFormatter: idToNameFormatter
        },
        [CustomColumnType.InsVariationColumn]: {
            cellClass: variationCellClass,
            comparator: customComparator,
            filter: "agTextColumnFilter"
        },
        [CustomColumnType.InsNumberSetColumn]: {
            cellClass: rightAlignCellClassName,
            filter: "agMultiColumnFilter",
            filterParams: {
                filters: [
                    {
                        filter: "agNumberColumnFilter",
                        filterParams: filterParamsTopBottom
                    },
                    {
                        filter: "agSetColumnFilter",
                        filterParams: {
                            valueFormatter: (p) => p.value?.toString()
                        }
                    }
                ]
            },
            headerClass: rightAlignHeaderClassName,
            type: CustomColumnType.InsNumberSetColumn
        },
        [CustomColumnType.InsTextSetColumn]: {
            filter: "agMultiColumnFilter",
            filterParams: {
                filters: [
                    {
                        comparator: customComparator,
                        filter: "agTextColumnFilter",
                        filterParams: {
                            buttons: ["reset"]
                        }
                    },
                    {
                        filter: "agSetColumnFilter",
                        filterParams: {
                            valueFormatter: (p) => p.value?.toString()
                        }
                    }
                ]
            },
            type: CustomColumnType.InsTextSetColumn
        },
        [CustomColumnType.InsDateBasedYearColumn]: {
            comparator: getDateComparator(),
            filter: "agTextColumnFilter",
            filterValueGetter: dateBasedYearGetter,
            valueFormatter: dateBasedYearFormatter
        },
        [CustomColumnType.InsChangeBenchmarkButtonColumn]: {
            cellClass: "center-aligned-cell",
            cellRenderer: "ChangeBenchmarkButtonCellRenderer",
            filter: false,
            headerClass: "center-aligned-header",
            sortable: false
        },
        [CustomColumnType.InsCheckBoxColumn]: {
            cellClass: "center-aligned-cell",
            cellRenderer: "CheckBoxCellRenderer",
            headerClass: "center-aligned-header",
            type: CustomColumnType.InsCheckBoxColumn
        },
        [CustomColumnType.InsQuartileRanking]: {
            cellClass: benchmarkQuartileCellClass,
            cellRenderer: "BenchmarkQuartileRenderer",
            filterParams: {
                valueFormatter: benchmarkQuartileRankingValueFormatter
            }
        },
        [CustomColumnType.InsBooleanToTextColumn]: {
            comparator: customComparator,
            filter: "agTextColumnFilter",
            filterParams: {
                buttons: ["reset"]
            },
            valueGetter: booleanGetter
        },
        [CustomColumnType.InsEditableColumn]: {
            cellClass: ["aux-editable-cell", "ag-cell-range-right", rightAlignCellClassName],
            editable: true
        },
        [CustomColumnType.InsRightBorderedColumn]: {
            cellClass: ["ag-cell-range-right"]
        },
        [CustomColumnType.InsCenterAllignedColumn]: {
            cellClass: "center-aligned-cell"
        },
        [CustomColumnType.InsAreaColumn]: {
            cellClass: rightAlignCellClassName,
            cellRenderer: "AreaCellRenderer",
            comparator: areaComparator,
            filter: "agTextColumnFilter",
            filterValueGetter: areaGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: areaFormatter
        },
        [CustomColumnType.InsUnitColumn]: {
            cellClass: rightAlignCellClassName,
            cellRenderer: "UnitCellRenderer",
            comparator: unitComparator,
            filter: "agTextColumnFilter",
            filterValueGetter: unitGetter,
            headerClass: rightAlignHeaderClassName,
            valueFormatter: unitFormatter
        },
        [CustomColumnType.InsYearsColumn]: {
            valueFormatter: yearsFormatter
        },
        [CustomColumnType.InsUserDateColumn]: {
            valueFormatter: userSettingAlignedDateFormatter
        },
        [CustomColumnType.InsTagsColumn]: {
            cellRenderer: "TagsCellRenderer",
            filter: "agSetColumnFilter",
            filterValueGetter: (params) => params.data.tags?.map((tag) => tag.label),
            type: [CustomColumnType.InsCenterAllignedColumn]
        },
        [CustomColumnType.InsPreqinProColumn]: {
            ...sharedDatapointLinkProperties
        },
        [CustomColumnType.AUX_TEXT_COLUMN]: {
            valueFormatter: textFormatter
        },
        [CustomColumnType.InsMultiValuePopupColumn]: {
            valueGetter: (params) => {
                const rawItems = params.data?.[params.colDef.field ?? ""];
                const { dataType } = params.colDef.cellRendererParams ?? {};
                const title = params.colDef.headerName ?? params.colDef.field ?? "";
                return buildMultiValuePopupData(rawItems, title, dataType ?? "string");
            }
        },
        [CustomColumnType.InsPortfolioKindColumn]: {
            filter: "agSetColumnFilter",
            filterParams: {
                valueFormatter: (params) => portfolioKindMap[params.value] ?? params.value
            },
            filterValueGetter: (params) => {
                const rawValue = params.data?.[params.colDef.field ?? ""];
                return portfolioKindMap[rawValue] ?? rawValue;
            },
            valueFormatter: (params) => portfolioKindMap[params.value] ?? params.value
        }
    };
    return customColumnTypes;
};
export const customDynamicColumnTypes = (customColumnType, performancePeriod) => {
    switch (customColumnType) {
        case CustomColumnType.InsPerformancePeriodColumn:
            return { comparator: performancePeriodComparator(performancePeriod) };
        default:
            return {};
    }
};
